import React, { useId, useRef, useState } from 'react';
import { Platform, TextInput, type TextInputProps } from 'react-native';
import { IconLock, IconX } from '@tabler/icons-react-native';

import { fieldInputReset } from '../field.styles';

import { Box, Text } from 'app/shopify/theme';

import {
  FieldAction,
  FieldBorder,
  FieldFrame,
  FieldIcon,
  useField,
  type FieldFrameProps,
  type FieldIconSlot,
  type FieldSize,
  type FieldState,
  type FieldValidation,
} from '../field';

export interface TextFieldProps extends Omit<TextInputProps, 'style'>, FieldFrameProps {
  size?: FieldSize;
  /** Optional preview state. Real hover and focus also update the field. */
  state?: FieldState;
  validation?: FieldValidation;
  disabled?: boolean;
  leadingIcon?: FieldIconSlot;
  actionIcon?: FieldIconSlot;
  onActionPress?: () => void;
  actionAccessibilityLabel?: string;
  showClearButton?: boolean;
  clearAccessibilityLabel?: string;
  prefix?: string;
  onClear?: () => void;
  inputStyle?: TextInputProps['style'];
  ref?: React.Ref<TextInput>;
}

export function TextField({
  label = 'Label',
  placeholder = 'Placeholder',
  helperText,
  errorMessage = 'Error message',
  size = 'default',
  state = 'enabled',
  validation = 'none',
  disabled = false,
  readOnly = false,
  editable = true,
  leadingIcon,
  actionIcon,
  onActionPress,
  actionAccessibilityLabel = 'Field action',
  showInfoIcon = false,
  onInfoPress,
  infoAccessibilityLabel,
  showClearButton = false,
  clearAccessibilityLabel = 'Clear text',
  prefix,
  value,
  defaultValue,
  onChangeText,
  onClear,
  onFocus,
  onBlur,
  accessibilityLabel,
  accessibilityHint,
  accessibilityState,
  nativeID,
  containerStyle,
  inputStyle,
  ref,
  ...inputProps
}: TextFieldProps) {
  const id = useId();
  const inputRef = useRef<TextInput>(null);
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const currentValue = value ?? uncontrolledValue;
  const field = useField({ size, state, validation, disabled, readOnly: readOnly || !editable });
  const { theme } = field;
  const description = field.error ? errorMessage : helperText;
  const labelId = `${id}-label`;
  const descriptionId = `${id}-description`;

  function changeText(text: string) {
    if (!field.interactive) return;
    if (value === undefined) setUncontrolledValue(text);
    onChangeText?.(text);
  }

  return (
    <FieldFrame
      field={field}
      label={label}
      helperText={helperText}
      errorMessage={errorMessage}
      showInfoIcon={showInfoIcon}
      onInfoPress={onInfoPress}
      infoAccessibilityLabel={infoAccessibilityLabel}
      labelId={labelId}
      descriptionId={descriptionId}
      containerStyle={containerStyle}
    >
      <Box
        minHeight={field.height}
        flexDirection="row"
        alignItems="center"
        gap="spacing.general.x1"
        paddingHorizontal="spacing.general.x3"
        paddingVertical="spacing.general.x1"
        borderRadius="radius.component.fields.input"
        backgroundColor="surface.default"
        {...(Platform.OS === 'web'
          ? { onMouseEnter: () => field.setHovered(true), onMouseLeave: () => field.setHovered(false) }
          : {})}
      >
        {leadingIcon ? <FieldIcon icon={leadingIcon} size={field.iconSize} color={field.iconColor} /> : null}
        <Box
          flex={1}
          minWidth={0}
          flexDirection="row"
          alignItems="center"
          gap="spacing.general.x0,5"
          paddingHorizontal="spacing.general.x1"
        >
          {prefix ? (
            <Text variant="bodyBase" numberOfLines={1} style={{ color: field.textColor }}>
              {prefix}
            </Text>
          ) : null}
          <TextInput
            {...inputProps}
            ref={(node) => {
              inputRef.current = node;
              if (typeof ref === 'function') return ref(node);
              if (ref) ref.current = node;
            }}
            nativeID={nativeID ?? id}
            value={currentValue}
            placeholder={placeholder}
            placeholderTextColor={theme.colors['text.tertiary']}
            editable={!field.isDisabled && !field.isReadOnly}
            readOnly={field.isReadOnly}
            {...getTextFieldAccessibility({
              accessibilityLabel,
              label,
              placeholder,
              accessibilityHint,
              description,
              accessibilityState,
              descriptionId,
              field,
            })}
            onChangeText={changeText}
            onFocus={(event) => {
              field.setFocused(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              field.setFocused(false);
              onBlur?.(event);
            }}
            style={[
              fieldInputReset.input,
              theme.textVariants[currentValue ? 'bodyBaseMedium' : 'bodyBase'],
              { color: field.textColor, minHeight: field.iconSize },
              inputStyle,
            ]}
          />
        </Box>
        <TextFieldActions
          field={field}
          currentValue={currentValue}
          showClearButton={showClearButton}
          clearAccessibilityLabel={clearAccessibilityLabel}
          actionIcon={actionIcon}
          actionAccessibilityLabel={actionAccessibilityLabel}
          onActionPress={onActionPress}
          clearText={() => {
            changeText('');
            onClear?.();
            inputRef.current?.focus();
          }}
        />
        <FieldBorder field={field} />
      </Box>
    </FieldFrame>
  );
}

function TextFieldActions({
  field,
  currentValue,
  showClearButton,
  clearAccessibilityLabel = 'Clear text',
  actionIcon,
  actionAccessibilityLabel = 'Field action',
  onActionPress,
  clearText,
}: Pick<
  TextFieldProps,
  'showClearButton' | 'clearAccessibilityLabel' | 'actionIcon' | 'actionAccessibilityLabel' | 'onActionPress'
> & {
  field: ReturnType<typeof useField>;
  currentValue: string;
  clearText: () => void;
}) {
  const { theme } = field;
  return (
    <>
      {showClearButton && currentValue && field.interactive ? (
        <FieldAction
          icon={<IconX strokeWidth={2} />}
          size={Number(theme.tokens.sizing['sizing.general.x8'])}
          iconSize={Number(theme.tokens.sizing['sizing.icon.sm'])}
          accessibilityLabel={clearAccessibilityLabel}
          onPress={clearText}
        />
      ) : null}
      {field.isReadOnly ? (
        <Box width={field.actionSize} alignItems="center">
          <IconLock size={field.iconSize} color={field.iconColor} strokeWidth={2} />
        </Box>
      ) : null}
      {actionIcon ? (
        <FieldAction
          icon={actionIcon}
          size={field.actionSize}
          iconSize={Number(theme.tokens.sizing['sizing.icon.default'])}
          accessibilityLabel={actionAccessibilityLabel}
          onPress={onActionPress}
          disabled={!field.interactive || !onActionPress}
        />
      ) : null}
    </>
  );
}

function getTextFieldAccessibility({
  accessibilityLabel,
  label,
  placeholder,
  accessibilityHint,
  description,
  accessibilityState,
  descriptionId,
  field,
}: Pick<TextFieldProps, 'accessibilityLabel' | 'label' | 'placeholder' | 'accessibilityHint' | 'accessibilityState'> & {
  description?: string;
  descriptionId: string;
  field: ReturnType<typeof useField>;
}) {
  return {
    accessibilityLabel: accessibilityLabel ?? label ?? placeholder,
    accessibilityHint: accessibilityHint ?? description,
    accessibilityState: { ...accessibilityState, disabled: field.isDisabled },
    'aria-invalid': field.error,
    ...(Platform.OS === 'web' ? { 'aria-describedby': description ? descriptionId : undefined } : {}),
  };
}
